/*
 * Einen KI-Vorschlag in einen offenen Entwurf einhaengen: reine Funktionen,
 * kein Preact, kein DOM, kein Netz.
 *
 * WARUM DAS EINE EIGENE DATEI IST UND NICHT EIN PAAR ZEILEN IM MODAL. Die
 * Stelle, an der das Anhaengen still schiefgeht, ist die Id. Der Server
 * vergibt die Widget-Ids eines Vorschlags deterministisch, aus einer
 * Streuung ueber Art, Beschriftung, Objektnamen und Bindungen (siehe
 * `SuggestedProjectBodyBuilder` auf der Serverseite). Zweimal derselbe
 * Vorschlag ergibt also zweimal dieselben Ids, und zwei Vorschlaege aus
 * demselben Anlagenbestand aehneln sich darin ebenfalls. Wer zwei Bilder
 * nacheinander anhaengt, hat deshalb keine seltene, sondern eine zu
 * erwartende Kollision vor sich.
 *
 * Eine doppelte Widget-Id macht keinen Krach. Sie macht aus zwei Kacheln in
 * jeder Nachschlagetabelle eine, laesst die Platzierung der einen auf die
 * andere zeigen, und der Server lehnt den Rumpf erst beim Speichern ab. Der
 * Autor sieht dann einen Fehler, dessen Ursache zehn Handgriffe zurueckliegt.
 * Genau dafuer steht die Umschluesselung hier und nicht verstreut im Modal:
 * sie ist die eine Zusage, die ein Test halten kann.
 *
 * KEINE LAUFZEIT-IMPORTE. Nur Typ-Importe, aus demselben Grund wie in
 * `hmi/editor/editorModel.ts` und `hmi/bindingKey.ts`: `npm test` faehrt
 * diese Datei direkt mit Node (`node --experimental-strip-types`), ohne
 * Bundler, und Node loest nur Pfade mit Endung auf. Ein Test, der nicht
 * laeuft, schuetzt nichts.
 */

import type {
  HmiProject,
  HmiScreen,
  ScreenLayout,
  WidgetDefinition,
  WidgetPlacement,
} from '../../hmi/widgets';

/** Die zwei Listen aus dem `bodyJson` des Vorschlags, ausgepackt. */
export interface SuggestedBody {
  widgets: readonly WidgetDefinition[];
  screens: readonly HmiScreen[];
}

export interface AppendSuggestionResult {
  /** Der neue Entwurf. Geht so, wie er ist, in `store.apply`. */
  project: HmiProject;
  /** Die neu entstandenen Bilder, in der Reihenfolge des Vorschlags. Das
   *  erste davon ist das, auf das die Oberflaeche hinschalten soll. */
  addedScreenIds: string[];
  /** Alte Id auf neue, nur fuer die Umbenannten. Leer heisst: keine
   *  Kollision, der Vorschlag steht unveraendert im Entwurf. */
  renamedWidgetIds: Record<string, string>;
  renamedScreenIds: Record<string, string>;
  /**
   * Platzierungen des Vorschlags, die zu keinem seiner Widgets gehoerten.
   *
   * Sollte leer sein, der Server baut den Rumpf selbst. Wenn nicht, ist das
   * Wegwerfen die richtige Antwort: eine verwaiste Platzierung ist genau der
   * Zustand, den derselbe Server beim Speichern ablehnt, und sie mitzunehmen
   * hiesse, den Fehler in den Entwurf des Autors zu tragen.
   */
  droppedPlacements: string[];
}

/**
 * Widgets und Bilder des Vorschlags an den offenen Entwurf anhaengen.
 *
 * Aendert `current` nicht, sondern gibt ein neues Projekt zurueck: das ist
 * die Voraussetzung dafuer, dass die Rueckgaengig-Kette des Stores ein
 * Stapel aus Zustaenden sein kann (siehe `hmi/editor/editorStore.ts`).
 *
 * Das Bild wird angehaengt und nicht in ein bestehendes hineingemischt. Ein
 * Vorschlag ist eine Anordnung, die als Ganzes gedacht ist; in ein
 * bestehendes Bild gestreut, laege er ueber gemachter Arbeit, und der Autor
 * muesste erst auseinanderhalten, was er selbst gelegt hat.
 */
export function appendSuggestion(
  current: HmiProject,
  suggested: SuggestedBody,
): AppendSuggestionResult {
  const takenWidgetIds = new Set<string>();
  for (const widget of current.widgets) takenWidgetIds.add(widget.id);
  // Auch die blossen Verweise der Bilder zaehlen als vergeben, obwohl hinter
  // manchen von ihnen kein Widget stehen mag. Ein Verweis ins Leere ist ein
  // Fehler, den das offene Projekt schon hat; eine neue Kachel auf genau
  // diese Id zu legen heilt ihn nicht, sondern haengt sie an eine Stelle in
  // einem fremden Bild, an die sie niemand gelegt hat.
  for (const screen of current.screens) {
    for (const widgetId of screen.widgetIds) takenWidgetIds.add(widgetId);
  }

  const takenScreenIds = new Set<string>(current.screens.map((s) => s.id));

  const renamedWidgetIds: Record<string, string> = {};
  const widgets: WidgetDefinition[] = [];
  for (const widget of suggested.widgets) {
    const id = uniqueId(widget.id, takenWidgetIds);
    takenWidgetIds.add(id);
    if (id !== widget.id) renamedWidgetIds[widget.id] = id;
    widgets.push(id === widget.id ? widget : ({ ...widget, id } as WidgetDefinition));
  }

  // Die Tabelle wird EINMAL gebaut und dann ueberall benutzt: Verweise der
  // Bilder und Schluessel der Platzierungen muessen dieselbe Antwort
  // bekommen, sonst entsteht beim Umbenennen genau die verwaiste Platzierung,
  // die das Umbenennen verhindern soll.
  const mapWidgetId = (old: string): string => renamedWidgetIds[old] ?? old;
  const knownWidgetIds = new Set<string>(widgets.map((w) => w.id));

  const renamedScreenIds: Record<string, string> = {};
  const droppedPlacements: string[] = [];
  const addedScreenIds: string[] = [];
  const screens: HmiScreen[] = [];
  for (const screen of suggested.screens) {
    const id = uniqueId(screen.id, takenScreenIds);
    takenScreenIds.add(id);
    if (id !== screen.id) renamedScreenIds[screen.id] = id;
    addedScreenIds.push(id);
    screens.push(rekeyScreen(screen, id, mapWidgetId, knownWidgetIds, droppedPlacements));
  }

  return {
    project: {
      ...current,
      widgets: [...current.widgets, ...widgets],
      screens: [...current.screens, ...screens],
    },
    addedScreenIds,
    renamedWidgetIds,
    renamedScreenIds,
    droppedPlacements,
  };
}

/**
 * Ein Bild mit neuer Id und umgeschluesselten Verweisen.
 *
 * Die Reihenfolge der Verweise bleibt, weil sie die Lesereihenfolge des
 * Vorschlags ist und ohne Anordnung (Auto-Flow) die einzige Auskunft
 * darueber, was zuerst kommt.
 */
function rekeyScreen(
  screen: HmiScreen,
  id: string,
  mapWidgetId: (old: string) => string,
  knownWidgetIds: ReadonlySet<string>,
  droppedPlacements: string[],
): HmiScreen {
  const widgetIds: string[] = [];
  for (const old of screen.widgetIds) {
    const next = mapWidgetId(old);
    // Ein Verweis auf ein Widget, das der Vorschlag gar nicht mitbringt,
    // waere im Entwurf ein Fehler mit fremdem Absender. Er faellt hier weg
    // und taucht als verworfene Platzierung im Bericht auf.
    if (knownWidgetIds.has(next)) widgetIds.push(next);
    else droppedPlacements.push(old);
  }

  const layouts: ScreenLayout[] = screen.layouts.map((layout) => {
    const placements: Record<string, WidgetPlacement> = {};
    for (const [old, placement] of Object.entries(layout.placements ?? {})) {
      const next = mapWidgetId(old);
      if (!knownWidgetIds.has(next)) {
        if (!droppedPlacements.includes(old)) droppedPlacements.push(old);
        continue;
      }
      placements[next] = placement;
    }
    return { ...layout, placements };
  });

  return { ...screen, id, widgetIds, layouts };
}

/**
 * Ein Name, den es in `taken` noch nicht gibt.
 *
 * Angehaengt wird eine Zahl und kein Zufall. Zwei Gruende: ein Test kann die
 * Zusage festnageln, ohne den Zufall zu ersetzen, und ein Mensch, der spaeter
 * ins gespeicherte JSON sieht, erkennt an `heat-circuit-tile-3okf-2` sofort,
 * welche der beiden Kacheln die spaeter hinzugekommene ist. Eine frische
 * Zufalls-Id (`freshId` in `editor/editorModel.ts`) verraet das nicht.
 *
 * Die Schleife ist unbegrenzt und hat trotzdem ein Ende: `taken` ist endlich,
 * also ist irgendeine Zahl frei.
 */
export function uniqueId(wanted: string, taken: ReadonlySet<string>): string {
  if (!taken.has(wanted)) return wanted;
  for (let n = 2; ; n += 1) {
    const candidate = `${wanted}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/**
 * Ein Projektname, den es in dieser Anlage noch nicht gibt.
 *
 * Der Server weist zwei Projekte gleichen Namens in derselben Anlage mit
 * HTTP 409 ab. Der Vorschlag liefert einen Bildnamen ("Uebersicht Haus"), und
 * genau der ist beim zweiten Lauf mit demselben Hinweis wieder derselbe. Dem
 * Autor dafuer eine Fehlermeldung hinzustellen, waere unnoetig: der
 * Vorbelegung eine Zahl anzuhaengen kostet nichts und er kann sie ohnehin
 * ueberschreiben.
 */
export function freeProjectName(wanted: string, taken: Iterable<string>): string {
  const trimmed = wanted.trim();
  const base = trimmed.length > 0 ? trimmed : 'HMI-Vorschlag';
  return uniqueId(base, new Set(taken));
}
